"use client";

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/shared/lib/api";
import { handleSilentError } from "@/shared/api/error-handler";
import { tournamentKeys } from "@/shared/api/query-keys";

/**
 * Шортлист капитана: кого он присмотрел в пуле. Хранится на бэкенде, поэтому
 * переживает смену устройства и виден и в списке участников, и в драфт-комнате.
 * Список личный — чужие шортлисты не отдаются.
 *
 * KAN-992: загрузка переехала из useEffect в useQuery. Ручной эффект не давал
 * ни дедупликации (хук зовут из двух мест сразу — пул и драфт-комната), ни
 * кэша между открытиями, а отмену при смене турнира приходилось писать руками.
 * Оптимистичное переключение осталось прежним, только теперь откат делает
 * onError мутации, а не ручной setState.
 */
export function useDraftShortlist(tournamentId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const queryKey = [...tournamentKeys.detail(tournamentId), "draft", "shortlist"];
  const isEnabled = Boolean(enabled && tournamentId);

  const { data: ids = EMPTY_SET, isFetched } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get(
        `/tournaments/${tournamentId}/draft/shortlist`,
      );
      return new Set<string>(data?.playerUserIds ?? []);
    },
    enabled: isEnabled,
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({
      playerUserId,
      isIn,
    }: {
      playerUserId: string;
      isIn: boolean;
    }) => {
      const url = `/tournaments/${tournamentId}/draft/shortlist/${playerUserId}`;
      if (isIn) await api.delete(url);
      else await api.post(url);
    },
    onMutate: async ({ playerUserId, isIn }) => {
      // Отклик мгновенный: список личный, конфликтовать не с кем.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Set<string>>(queryKey);
      queryClient.setQueryData<Set<string>>(queryKey, (old) => {
        const next = new Set(old ?? []);
        if (isIn) next.delete(playerUserId);
        else next.add(playerUserId);
        return next;
      });
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      handleSilentError(error, {
        component: "useDraftShortlist",
        action: "toggle",
      });
    },
  });

  // KAN-986: в зависимостях именно mutate — он стабилен, а сам объект мутации
  // новый на каждый рендер и ломал memo у пула игроков.
  const { mutate } = toggleMutation;
  const toggle = useCallback(
    (playerUserId: string) => {
      if (!tournamentId) return;
      mutate({ playerUserId, isIn: ids.has(playerUserId) });
    },
    [ids, tournamentId, mutate],
  );

  return {
    shortlist: ids,
    toggleShortlist: toggle,
    // Пока хук выключен, «загружено» не наступает — как и раньше.
    shortlistLoaded: isEnabled ? isFetched : false,
  };
}

/** Стабильная пустая ссылка: иначе useQuery отдавал бы новый Set на каждый рендер. */
const EMPTY_SET: Set<string> = new Set<string>();
